import React, { useEffect, useTransition } from "react";
import "react-quill/dist/quill.snow.css"; // Quill's default theme
import styles from "./TextEditor.module.css"; // Import CSS module for custom styles
import { set } from "react-datepicker/dist/date_utils";
import { postData } from "../../utilities/networkCalls";
import config from "../../config";
import { Modal } from "@shopify/polaris";

type TextEditorProps = {
    value: string;
    onChange: (val: string) => void;
    placeholder?: string;
    className?: string; // Optional custom class for external styles
    readOnly?: boolean;
    error?: string;
};

const TextEditor = ({
    value,
    onChange,
    placeholder = "Enter text...",
    className = "",
    readOnly = false,
    error,
}: TextEditorProps) => {
    const [initialized, setInitialized] = React.useState(false);
    const [isPending, startTransition] = useTransition();
    const [uploading, setUploading] = React.useState(false);

    const uploadImageHandler = async (file: any) => {
        setUploading(true);
        const data = new FormData();
        data.append("file", file);

        const res = await postData({
            url: `/${config.appName}/upload`,
            payload: data,
        });
        setUploading(false);
        if (res.status !== 200) {
            throw new Error("Image upload failed");
        }

        return res.data.url;
    };

    const editorChangeHandler = () => {
        startTransition(() => {
            resizeEditor();
            onChange(window.tinymce.activeEditor.getContent());
        });
    };

    const resizeEditor = () => {
        try {
            const contentHeight =
                window.tinymce.activeEditor.iframeElement.contentWindow.document.body.clientHeight + 100;
            console._log("Content height:", contentHeight);
            window.tinymce.activeEditor.container.style.height =
                (contentHeight < 200 ? 200 : Math.min(contentHeight, 500)) + "px";
        } catch (error) {
            console._log("Error resizing editor:", error);
        }
    };
    useEffect(() => {
        window.tinymce.init({
            toolbar_mode: "wrap",
            license_key: "gpl",
            selector: "textarea#kefi-editor",
            height: 200,
            menubar: false,
            statusbar: false,
            plugins: [
                "advlist",
                "autolink",
                "lists",
                "link",
                "image",
                "charmap",
                "preview",
                "anchor",
                "searchreplace",
                "visualblocks",
                "code",
                "fullscreen",
                "insertdatetime",
                "media",
                "table",
                "wordcount",
                "emoticons",
                "imagetools",
            ],
            toolbar1: ` blocks | bold italic underline backcolor forecolor| alignleft aligncenter alignright alignjustify | code `,
            toolbar2: `link image media  | bullist numlist outdent indent table | fontsizeinput | removeformat 
        `,

            content_style: "body { font-family:sans-serif; font-size:13px}",
            image_title: false,
            /* enable automatic uploads of images represented by blob or data URIs*/
            automatic_uploads: false,
            placeholder: placeholder,

            /*
URL of our upload handler (for more details check: https://www.tiny.cloud/docs/configure/file-image-upload/#images_upload_url)
images_upload_url: 'postAcceptor.php',
here we add custom filepicker only to Image dialog
*/
            file_picker_types: "image",
            /* and here's our custom image picker*/
            file_picker_callback: (cb) => {
                const input = document.createElement("input");
                input.setAttribute("type", "file");
                input.setAttribute("accept", "image/*");

                input.addEventListener("change", (e) => {
                    const file = e.target.files[0];

                    const reader = new FileReader();
                    reader.addEventListener("load", async () => {
                        /*
Note: Now we need to register the blob in TinyMCEs image blob
registry. In the next release this part hopefully won't be
necessary, as we are looking to handle it internally.
*/
                        const id = "blobid" + new Date().getTime();
                        const blobCache = window.tinymce.activeEditor.editorUpload.blobCache;
                        const base64 = reader.result.split(",")[1];
                        const blobInfo = blobCache.create(id, file, base64);

                        blobCache.add(blobInfo);
                        const url = await uploadImageHandler(file);
                        /* call the callback and populate the Title field with the file name */
                        cb(url, { title: file.name });
                    });
                    reader.readAsDataURL(file);
                });

                input.click();
            },
        });

        window.tinymce.activeEditor.on("init", () => {
            setInitialized(true);
        });
        window.tinymce.activeEditor.on("input", () => {
            editorChangeHandler();
        });
        window.tinymce.activeEditor.on("Change", () => {
            editorChangeHandler();
        });
        window.tinymce.activeEditor.on("ObjectResized", () => {
            editorChangeHandler();
        });

        window.tinymce.activeEditor.on("Load", () => {
            setTimeout(resizeEditor, 1000);
        });
        window.tinymce.activeEditor.on("drop", (e) => {
            e.preventDefault();
            e.stopPropagation();
        });

        return () => {
            window.tinymce?.activeEditor?.destroy();
        };
    }, []);

    useEffect(() => {
        if (initialized) {
            window.tinymce.activeEditor.mode.set(readOnly ? "readonly" : "design");
        }
    }, [readOnly, initialized]);

    useEffect(() => {
        const imageDialog = document.querySelector(".tox-dialog");

        if (imageDialog) {
            imageDialog.querySelectorAll("input").forEach((input) => {
                input.disabled = uploading;
            });
            imageDialog.querySelectorAll("button").forEach((button) => {
                button.disabled = uploading;
            });

            const dialogTitle = imageDialog.querySelector(".tox-dialog__title");
            if (dialogTitle) {
                dialogTitle.innerHTML = uploading ? "Uploading Image..." : "Insert/Edit Image";
            }
        }
    }, [uploading]);

    return (
        <div className={`${styles.textEditorWrapper} ${className}`}>
            <textarea id="kefi-editor">{value}</textarea>

            {error}
        </div>
    );
};

export default TextEditor;
